/**
 * Panel pembayaran — **tender pecah** (§7).
 *
 * ### ⭐ Kenapa `tender_type` ditampilkan, bukan disembunyikan
 * Kasir perlu tahu bahwa "QRIS BRI" bukan uang di laci. Label diambil dari kamus
 * `enum.tenderType.*` — **bukan ditebak dari nama metode bayar**, karena nama adalah tampilan dan
 * `tender_type` adalah maknanya (`design/I18N.md` §3). Inilah yang membuat gerbang
 * `i18n-enum-check` berguna: nilai enum tanpa label akan muncul mentah di layar kasir.
 *
 * ### ⛔ Tombol tutup TIDAK bisa ditekan sebelum lunas
 * Invarian §7 (`Σ tender = grand_total`) sudah ditegakkan server, tapi menampilkan tombol yang
 * pasti gagal adalah cara memancing kasir mengulang — dan pengulangan di depan tamu adalah sumber
 * transaksi ganda. Layar menolak lebih dulu; server tetap menolak sebagai jaring pengaman.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PaymentMethod } from '../api/client';

export interface TenderPanelProps {
  /**
   * ⭐⭐ Potongan yang SUDAH terpasang — ditampilkan sebagai chip yang **menetap** sampai
   * transaksi tutup.
   *
   * ⛔ Diskon yang sudah dipasang tapi **tak terlihat** adalah persis asal "lupa diskon" — sebab
   * yang Imron sebut saat meminta fitur recall. Menampilkannya di layar bayar **mencegah** recall,
   * bukan sekadar memudahkannya; dan recall yang dicegah adalah bill lunas yang tak perlu
   * dibongkar sama sekali.
   */
  readonly discountTotal?: string;
  readonly grandTotal: string;
  readonly methods: readonly PaymentMethod[];
  readonly paidAmount: string;
  readonly busy: boolean;
  /**
   * ⭐⭐⭐ `tenderedAmount` = **uang yang diserahkan tamu**, terpisah dari `amount` yang diterapkan
   * ke bill. Hanya terisi untuk TUNAI yang berlebih; server menolak selebihnya (0060).
   *
   * ⛔ Dua parameter, bukan satu yang "kadang lebih besar": Σ `amount` = `grand_total` adalah
   * invarian §7, dan satu nilai yang memikul dua arti akan cepat atau lambat masuk ke penjumlahan
   * yang salah.
   */
  readonly onPay: (
    m: PaymentMethod, amount: string, reference?: string, tenderedAmount?: string,
    /**
     * ⭐⭐⭐ **Seam EDC — mode MANUAL** (K7). Diisi kasir dari slip yang sudah di tangannya.
     *
     * ⛔ Satu OBJEK, bukan dua parameter posisi lagi: daftar parameter yang terus memanjang akan
     * ditulis salah urutan cepat atau lambat, dan approval yang mendarat di kolom `card_last4`
     * tak bergejala sampai setoran bank tak bisa dijodohkan.
     */
    edc?: { approvalCode?: string; cardLast4?: string },
  ) => void;
  readonly onClose: () => void;
  /**
   * ⭐⭐⭐ **Batalkan tagihan** — kembali menyunting pesanan (Imron, 2026-08-23: *"ketika tekan
   * tombol pay yang pertama, tidak ada tombol cancel-nya"*).
   *
   * ⛔ Ini BUKAN void: yang dibatalkan cuma hitungannya, barisnya utuh. ⚠️ `undefined` ⇒ tombolnya
   * tak muncul — dipakai saat sudah ada tender tercatat, di mana pembatalan memang bukan lagi
   * jawabannya (server pun menolaknya).
   */
  readonly onCancelBill?: () => void;
}

/** String desimal kanonik → angka rupiah bulat. ⚠️ Hanya untuk TAMPILAN & masukan kasir. */
function rp(canonical: string): number {
  return Number(canonical.split('.')[0] ?? '0');
}
function keKanonik(n: number): string {
  return `${String(Math.round(n))}.0000`;
}
function format(n: number): string {
  return `Rp ${String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}

export function TenderPanel(p: TenderPanelProps) {
  const { t } = useTranslation();
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  /** ⭐ Kode approval dari slip EDC — WAJIB untuk metode ber-`viaEdc` (K7). */
  const [approval, setApproval] = useState('');
  /**
   * ⭐⭐⭐ **Mode BAGI BAYAR** — semua metode terlihat sekaligus, isi yang dipakai.
   *
   * Imron menunjuk `osbbilling`: satu dialog, satu kolom per metode, satu indikator
   * **Kurang / Kembalian / Pas**. Bentuk itu jauh lebih jelas untuk kasus paling sering ("100 ribu
   * tunai, sisanya QRIS") daripada alur berurutan — yang sebenarnya SUDAH jalan di sini, tapi
   * menuntut kasir menyadari bahwa mengetik angka lebih kecil itu boleh. Yang tak terlihat, tak
   * dipakai.
   *
   * ⛔ Metodenya tetap dari KATALOG, tak dipaku "Cash + QRIS" seperti di `osbbilling`: outlet yang
   * menambah metode bayar tak boleh perlu rilis aplikasi.
   */
  const [splitMode, setSplitMode] = useState(false);
  const [splitAmounts, setSplitAmounts] = useState<Record<string, string>>({});
  /** ⛔ 4 digit TERAKHIR saja. Nomor kartu penuh dilarang (PCI) — server & DB menolaknya juga. */
  const [last4, setLast4] = useState('');
  /**
   * Kelompok `tender_type` aktif. `null` = semua — dipilih sendiri bila hanya ada satu kelompok,
   * supaya outlet sederhana tak dipaksa mengetuk dua kali untuk satu-satunya pilihan.
   */
  const [kind, setKind] = useState<string | null>(null);

  /** Kelompok yang benar-benar ADA di outlet ini; urutannya mengikuti katalog dari server. */
  const groups = useMemo(() => {
    const out: string[] = [];
    for (const m of p.methods) if (!out.includes(m.tenderType)) out.push(m.tenderType);
    return out;
  }, [p.methods]);

  const visibleMethods = useMemo(
    () => (kind === null ? p.methods : p.methods.filter((m) => m.tenderType === kind)),
    [kind, p.methods],
  );

  const remaining = useMemo(() => rp(p.grandTotal) - rp(p.paidAmount), [p.grandTotal, p.paidAmount]);
  const settled = remaining <= 0;
  const value = amount === '' ? remaining : Number(amount);
  const needsReference = method?.requiresReference === true;
  const referenceMissing = needsReference && reference.trim() === '';
  /*
   * ⭐⭐⭐ **Metode lewat EDC ⇒ approval WAJIB** (K7). Datang dari SERVER lewat katalog
   * (`payment_method.via_edc`), tak ditebak dari nama metode — "BCA DEBIT" vs "QRIS" adalah
   * konvensi-nama yang Guardrail #2 larang, dan yang salah tebak berarti approval tak pernah
   * diminta untuk metode yang membutuhkannya.
   */
  const viaEdc = method?.viaEdc === true;
  const approvalMissing = viaEdc && approval.trim() === '';
  /* ⛔ 4 digit, atau kosong. Yang setengah terisi ditolak DI SINI supaya tak jadi galat server. */
  const last4Bad = last4.trim() !== '' && !/^[0-9]{4}$/.test(last4.trim());
  const canPay = method !== null && value > 0 && !referenceMissing && !approvalMissing
    && !last4Bad && !p.busy;

  /*
   * ⭐⭐⭐ **Untuk TUNAI, yang diketik kasir adalah UANG YANG DITERIMA** — dan itu memang yang ia
   * lakukan: tamu menyerahkan 200 ribu untuk bill 117.810, kasir mengetik 200000.
   *
   * ⛔ Sampai 0060, angka itu masuk ke `amount` dan penutupannya DITOLAK server (Σ tender =
   * grand_total) — sehingga kasir wajib mengetik 117810, angka yang tak dipegang siapa pun.
   * Sekarang: yang diterapkan tetap sisanya, kelebihannya jadi `tenderedAmount`.
   *
   * ⚠️ Non-tunai TIDAK diperlakukan begitu: kembalian keluar dari laci, dan kartu tak punya laci.
   */
  /*
   * ⭐ Hitungan mode bagi-bayar: Σ yang diisi vs sisa tagihan. Tiga keadaan yang DIBEDAKAN —
   * kurang · pas · kembalian — persis seperti `osbbilling`, karena ketiganya menuntut tindakan
   * berbeda dari kasir dan menyamakannya membuat dua di antaranya tak terlihat.
   */
  const splitTotal = p.methods.reduce((n, m) => n + Number(splitAmounts[m.code] ?? '0'), 0);
  const splitKurang = remaining - splitTotal;
  /* ⛔ Kembalian hanya sah dari TUNAI — kartu tak punya laci. Kelebihan pada metode non-tunai
     bukan kembalian melainkan salah ketik, dan ia ditolak sebelum satu tender pun lahir. */
  const splitLebihNonTunai = p.methods.some(
    (m) => m.tenderType !== 'cash' && Number(splitAmounts[m.code] ?? '0') > 0
      && splitTotal - remaining > 0
      && Number(splitAmounts[m.code] ?? '0') > remaining,
  );
  const splitBisa = splitTotal > 0 && splitKurang <= 0 && !splitLebihNonTunai;

  const tunai = method?.tenderType === 'cash';
  const applied = tunai ? Math.min(value, remaining) : value;
  const change = tunai ? Math.max(0, value - remaining) : 0;

  return (
    <section className="tender" aria-label={t('cashier.pay.title')} data-testid="tender">
      <h2>{t('cashier.pay.title')}</h2>

      {/*
        ⭐⭐ Chip potongan MENETAP — tak hilang saat metode berganti, tak hilang saat sebagian
        sudah dibayar. Potongan yang terpasang tapi tak terlihat adalah asal "lupa diskon", dan
        yang menampilkannya MENCEGAH panggil-balik bill lunas, bukan sekadar memudahkannya.
      */}
      {p.discountTotal !== undefined && rp(p.discountTotal) > 0 && (
        <p className="tender-discount" data-testid="tender-discount">
          {t('cashier.pay.discountApplied', { amount: format(rp(p.discountTotal)) })}
        </p>
      )}

      <p className={settled ? 'remaining settled' : 'remaining'} data-testid="remaining">
        {settled ? t('cashier.pay.settled') : t('cashier.pay.short', { amount: format(remaining) })}
      </p>

      {/*
        ⭐⭐⭐ **Sakelar BAGI BAYAR.** Alur berurutan (pilih metode → ketik sebagian → Bayar → ulangi)
        sudah jalan sejak awal, tapi menuntut kasir MENYADARI bahwa mengetik angka lebih kecil itu
        boleh — dan yang tak terlihat, tak dipakai. Bentuk `osbbilling` menampilkan semuanya
        sekaligus, dan kasir tinggal mengisi yang dipakai.
      */}
      {!settled && p.methods.length > 1 && (
        <button
          type="button"
          className={splitMode ? 'level-chip on' : 'level-chip'}
          data-testid="split-toggle"
          onClick={() => { setSplitMode((v) => !v); setSplitAmounts({}); }}
        >
          {t('cashier.pay.split')}
        </button>
      )}

      {!settled && splitMode && (
        <div className="tender-split" data-testid="tender-split">
          {p.methods.map((m) => (
            <label key={m.code} className="split-row">
              <span className="split-name">{m.name}</span>
              <input
                type="number"
                inputMode="numeric"
                data-testid={`split-${m.code}`}
                value={splitAmounts[m.code] ?? ''}
                onChange={(e) => {
                  setSplitAmounts((v) => ({ ...v, [m.code]: e.target.value }));
                }}
              />
            </label>
          ))}

          {/*
            ⭐⭐ Tiga keadaan yang DIBEDAKAN — kurang · pas · kembalian. Ketiganya menuntut tindakan
            berbeda dari kasir, dan menyamakannya membuat dua di antaranya tak terlihat.
          */}
          <p
            className={splitKurang > 0 ? 'error' : 'done'}
            data-testid="split-remaining"
          >
            {splitKurang > 0
              ? t('cashier.pay.splitShort', { amount: format(splitKurang) })
              : splitKurang < 0
                ? t('cashier.pay.splitChange', { amount: format(-splitKurang) })
                : t('cashier.pay.splitExact')}
          </p>

          {/* ⛔ Kelebihan pada metode NON-TUNAI bukan kembalian melainkan salah ketik: kartu tak
              punya laci, dan server menolaknya (`ORDER-409-TENDERED_NOT_CASH`). Ditolak di sini
              supaya penolakan itu tak perlu terjadi di depan tamu. */}
          {splitLebihNonTunai && (
            <p className="error" data-testid="split-nontunai-lebih">
              {t('cashier.pay.splitNonCashOver')}
            </p>
          )}

          <button
            type="button"
            className="action-primary"
            data-testid="split-pay"
            disabled={!splitBisa || p.busy}
            onClick={() => {
              /*
               * ⭐ Dikirim SATU PER SATU lewat `onPay` yang sama — bukan jalur baru. Server sudah
               * menegakkan invarian §7 per tender; menambah endpoint "bayar borongan" berarti dua
               * jalan menuju satu kebenaran, dan yang kedua akan menyimpang.
               * ⚠️ TUNAI didahulukan supaya kelebihannya jatuh sebagai KEMBALIAN di sana, bukan di
               * metode non-tunai yang tak punya laci.
               */
              const urut = [...p.methods].sort(
                (a2, b2) => (b2.tenderType === 'cash' ? 1 : 0) - (a2.tenderType === 'cash' ? 1 : 0),
              );
              let sisa = remaining;
              for (const m of urut) {
                const v = Number(splitAmounts[m.code] ?? '0');
                if (v <= 0) continue;
                const dipakai = m.tenderType === 'cash' ? Math.min(v, sisa) : v;
                p.onPay(
                  m,
                  keKanonik(dipakai),
                  undefined,
                  m.tenderType === 'cash' && v > sisa ? keKanonik(v) : undefined,
                  undefined,
                );
                sisa -= dipakai;
              }
              setSplitAmounts({});
              setSplitMode(false);
            }}
          >
            {t('cashier.action.pay')}
          </button>
        </div>
      )}

      {!settled && !splitMode && (
        <>
          {/*
            ⭐⭐ **Dikelompokkan menurut `tender_type`, bukan menurut nama merek.**

            Guardrail #2: klasifikasi ekonomi itu STRUKTURAL. Kasir perlu tahu "QRIS BRI" bukan
            uang di laci — dan itu sifat `tender_type`, bukan sifat namanya.

            ⛔ Sengaja TIDAK meniru pengelompokan pesaing ("Non Tunai (Debit/Kredit)" dipisah dari
            "Pembayaran Online"): kartu debit dan QRIS **sama-sama `clearing`** — uang datang
            belakangan lewat penyelenggara, dengan MDR. Mengelompokkan menurut ALAT adalah persis
            kesalahan VTEC, di mana satu boolean memikul tiga kategori ekonomi.
          */}
          {groups.length > 1 && (
            <nav className="tender-kinds" aria-label={t('cashier.pay.chooseMethod')}>
              {groups.map((g) => (
                <button
                  key={g}
                  type="button"
                  className={kind === g ? 'level-chip on' : 'level-chip'}
                  data-testid={`kind-${g}`}
                  onClick={() => { setKind(g); setMethod(null); setReference(''); }}
                >
                  {t(`enum.tenderType.${g}`)}
                </button>
              ))}
            </nav>
          )}

          <div className="method-grid" aria-label={t('cashier.pay.chooseMethod')}>
            {visibleMethods.map((m) => (
              <button
                key={m.code}
                type="button"
                className={method?.code === m.code ? 'method on' : 'method'}
                data-testid={`method-${m.code}`}
                data-tender-type={m.tenderType}
                onClick={() => {
                  setMethod(m);
                  setReference('');
                }}
              >
                <span className="method-name">{m.name}</span>
                {/* ⭐ Makna ekonominya, bukan sekadar namanya. */}
                <span className="method-kind">{t(`enum.tenderType.${m.tenderType}`)}</span>
                {/*
                  ⭐ **Nominal yang akan dibayar ikut di tombolnya** (dari referensi kartu gelap).
                  Kasir memilih metode sambil menyebut angkanya ke tamu; kalau angkanya cuma ada di
                  baris "kurang", ia harus melihat dua tempat dalam satu tarikan napas.
                  ⚠️ Hanya saat masih ada sisa — menempelkan "Rp 0" di tiap tombol pada bill lunas
                  cuma menambah angka yang harus diabaikan.
                */}
                {remaining > 0 && (
                  <span className="method-amount" data-testid={`method-amount-${m.code}`}>
                    {format(remaining)}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="tender-input">
            <input
              type="number"
              inputMode="numeric"
              data-testid="amount"
              placeholder={format(remaining)}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <button
              type="button"
              data-testid="exact-cash"
              onClick={() => setAmount(String(remaining))}
            >
              {t('cashier.exactCash')}
            </button>
          </div>

          {/*
            ⭐⭐ **Kembalian terbaca SEBELUM tombol Bayar ditekan.** Kasir menyebut angkanya sambil
            mengambil uang dari laci; kalau ia baru muncul sesudah bayar, ia menghitungnya di kepala
            lebih dulu — dan hitungan di kepala di depan antrean adalah asal selisih kas.
            ⛔ Hanya saat memang ada kembalian: baris "Kembali Rp 0" cuma angka yang harus diabaikan.
          */}
          {change > 0 && (
            <p className="tender-change" data-testid="tender-change">
              {t('cashier.pay.change', { amount: format(change) })}
            </p>
          )}

          {needsReference && (
            <div className="tender-reference">
              <input
                data-testid="reference"
                placeholder={t('cashier.pay.referenceNo')}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
              {referenceMissing && (
                <span className="error" data-testid="reference-required">
                  {t('cashier.pay.referenceRequired')}
                </span>
              )}
            </div>
          )}

          {/*
            ⭐⭐⭐ **Kotak EDC** — muncul HANYA untuk metode yang memang lewat mesin gesek.

            ⛔ Ini yang selama ini tak ada, dan akibatnya persis yang riset kita hukum: kiosk Arena
            sudah mengurai approval/RRN/terminal/batch dari EDC BCA dengan benar lalu MEMBUANGnya,
            dan VTEC menulis `CCApproveCode` kosong padahal kolomnya ada. Hasil keduanya sama —
            setoran bank tak bisa dijodohkan dengan struk.

            ⚠️ Kasir tak diminta mengarang apa pun: kode approval TERCETAK di slip yang sudah ada
            di tangannya sebelum ia menyentuh layar ini.
          */}
          {viaEdc && (
            <div className="tender-edc" data-testid="tender-edc">
              <input
                data-testid="approval-code"
                inputMode="numeric"
                maxLength={20}
                placeholder={t('cashier.pay.approvalCode')}
                value={approval}
                onChange={(e) => setApproval(e.target.value)}
              />
              {/* ⛔ Opsional — 4 digit terakhir menolong rekonsiliasi, tapi memaksanya membuat
                  kasir mengetik dari kartu tamu yang sudah masuk dompet lagi. */}
              <input
                data-testid="card-last4"
                inputMode="numeric"
                maxLength={4}
                placeholder={t('cashier.pay.cardLast4')}
                value={last4}
                onChange={(e) => setLast4(e.target.value.replace(/[^0-9]/g, ''))}
              />
              {approvalMissing && (
                <span className="error" data-testid="approval-required">
                  {t('cashier.pay.approvalRequired')}
                </span>
              )}
              {last4Bad && (
                <span className="error" data-testid="card-last4-bad">
                  {t('cashier.pay.cardLast4Bad')}
                </span>
              )}
            </div>
          )}

          <button
            type="button"
            className="action-primary"
            data-testid="pay"
            disabled={!canPay}
            onClick={() => {
              if (method === null) return;
              p.onPay(
                method,
                keKanonik(applied),
                reference.trim() === '' ? undefined : reference,
                /* ⛔ Dikirim HANYA bila ada kembalian. `tendered == amount` menulis kolom tanpa
                   menambah satu pun jawaban, dan menghapus arti NULL ("tak dicatat"). */
                change > 0 ? keKanonik(value) : undefined,
                viaEdc
                  ? {
                      approvalCode: approval.trim(),
                      ...(last4.trim() === '' ? {} : { cardLast4: last4.trim() }),
                    }
                  : undefined,
              );
              setAmount('');
              setReference('');
              setApproval('');
              setLast4('');
            }}
          >
            {t('cashier.action.pay')}
          </button>
        </>
      )}

      {/*
        ⭐⭐⭐ **BATAL** — jalan pulang ke menyunting pesanan (Imron, 2026-08-23).
        ⛔ Bukan void: barisnya utuh, cuma hitungannya yang dibatalkan. ⚠️ Menghilang begitu ada
        tender tercatat — di sana pembatalan memang bukan lagi jawabannya, dan server menolaknya.
      */}
      {!settled && p.onCancelBill !== undefined && rp(p.paidAmount) === 0 && (
        <button type="button" data-testid="cancel-bill" disabled={p.busy}
                onClick={p.onCancelBill}>
          {t('cashier.action.cancelBill')}
        </button>
      )}

      {/* ⛔ Hanya muncul saat lunas — tombol yang pasti gagal memancing kasir mengulang. */}
      {settled && (
        <button
          type="button"
          className="action-primary"
          data-testid="close-order"
          disabled={p.busy}
          onClick={p.onClose}
        >
          {t('common.close')}
        </button>
      )}
    </section>
  );
}
